<template>
  <div class="d-flex flex-column justify-content-start" style="height: 100%">
    <!-- First Window: Timetable List -->
    <v-card v-if="!showTimetableDetails && !showIrregularTimetableDetails" class="d-flex flex-column flex" style="height: 100%">
      <v-card-text>
        <div class="">
          <div class="d-flex flex-column align-items-start mr-4" style="align-items: end;">
            <div class="d-flex align-items-center justify-end">
              <v-autocomplete
                dense
                outlined
                placeholder="Select Session"
                v-model="selectedSession"
                return-object
                item-text="name"
                :items="sessions"
                style="max-width: 30%;"
              />

              <v-select
                dense
                outlined
                placeholder="Select Semester"
                v-model="selectedSemester"
                :items="selectedSession?.semesters"
                item-text="title"
                return-object
                class="mx-2"
                style="max-width: 30%;"
              />
            </div>
          </div>
        </div>

        <!-- Grid Layout for Timetable -->
        <div class="timetable-grid mt-4" v-if="!loading && timetables.length">
          <v-row>
            <v-col v-for="(timetable, index) in withSno" :key="index" cols="12" sm="6" md="4" lg="3">
              <v-card 
                :class="['timetable-card', { 'timetable-card--pending': timetable.timeTableStatus === 'PENDING' }]" 
                elevation="2" 
                @click="timetable.timeTableStatus !== 'PENDING' && handleRowClick(timetable)"
              >
                <v-card-title class="text-subtitle-1">
                  {{ timetable.name }}
                </v-card-title>
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <v-icon small color="primary" class="mr-2">mdi-school</v-icon>
                    <span class="text-caption">{{ timetable.programme?.name }}</span>
                  </div>
                  <div class="d-flex align-center mb-2">
                    <v-icon small color="primary" class="mr-2">mdi-book-education</v-icon>
                    <span class="text-caption">{{ timetable.semester?.title + ' Semester' }}</span>
                  </div>
                  <div class="d-flex align-center">
                    <v-icon small color="primary" class="mr-2">mdi-format-list-numbered</v-icon>
                    <span class="text-caption">{{ timetable.level?.title }}</span>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-chip small :color="getStatusColor(timetable.timeTableStatus)" text-color="white">
                    {{ timetable.timeTableStatus }}
                  </v-chip>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Empty State -->
        <v-alert v-if="!loading && !timetables.length" type="info" class="mt-4">
          No timetables found for the selected criteria.
        </v-alert>

      </v-card-text>
    </v-card>

    <!-- Regular Timetable Details View -->
    <timetable-details-view
      v-if="showTimetableDetails"
      :timetable="selectedTimetable"
      :is-student="true"
      @back="showTimetableDetails = false"
    />

    <!-- Irregular Timetable Details View -->
    <irregular-timetable-details-view
      v-if="showIrregularTimetableDetails"
      :timetable="selectedTimetable"
      :is-student="true"
      @back="showIrregularTimetableDetails = false"
    />

    <loading-dialog :show="loading"></loading-dialog>
  </div>
</template>

<script>
import Api from 'main/api';
import ErrorResolver from 'shared/ErrorResolver';
import { mapGetters } from 'vuex'
import LoadingDialog from "@ui/components/LoadingDialog";
import TimetableDetailsView from "@ui/officer/components/TimetableDetailsView.vue";
import IrregularTimetableDetailsView from "@ui/officer/components/IrregularTimetableDetailsView.vue";

export default {
  name: 'TimetablePage',
  components: {
    LoadingDialog,
    TimetableDetailsView,
    IrregularTimetableDetailsView,
  },

  data() {
    return {
      loading: false,
      page: 1,
      totalElements: 0,
      selectedSemester: null,
      selectedSession: null,
      sessions: [],
      timetables: [],
      showTimetableDetails: false,
      showIrregularTimetableDetails: false,
      selectedTimetable: null,
    }
  },

  methods: {
    handleRowClick(item) {
      this.selectedTimetable = item;
      if (item.timeTableType === 'IRREGULAR') {
        this.showIrregularTimetableDetails = true;
      } else {
        this.showTimetableDetails = true;
      }
    },

    async loadTimeTables() {
      if(!this.selectedSemester || !this.getUser)
      this.loading = true;

        Api.timetable.getTimetableForStudent(this.getUser.userId, this.selectedSemester.id)
            .then((res) => {
                // console.log('tables:', res.data)
                this.timetables = res.data;
            })
            .catch((err) => ErrorResolver.checkErrors(this, err))
            .finally(() => {
                this.loading = false;
            })
    },

    getStatusColor(status) {
      const colors = {
        ACTIVE: 'success',
        INACTIVE: 'grey',
        DRAFT: 'warning',
        PENDING: 'grey lighten-1'
      }
      return colors[status] || 'primary'
    },

    getSessions(){
      if(this.getUser != null){
        Api.session.getRegisteredSessionsForStudent(this.getUser.userId)
        .then(res => {
          this.sessions = res.data
          // console.log(res.data)
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
      }
    }
  },

  computed: {
    ...mapGetters(['getCurrentProgrammeType', 'getUser', 'getCurrentSession']),

    withSno() {
      return this.timetables.map((item, index) => ({
        ...item,
        sno: index + 1 + (this.page - 1) * 10
      }))
    }
  },

  watch: {
    getCurrentProgrammeType(val) {
      if (val) {
        this.loadProgrammes()
      }
    },

    getCurrentSession(val) {
      if (val) {
        this.semesters = val.semesters
      }
    },

    selectedSemester(val) {
      this.timetables = []
      if (val) {
        this.loadTimeTables()
      }
    }
  },

  created() {
    this.getSessions()
  }
}
</script>

<style scoped>
.timetable-page {
  background-color: #f5f5f5;
}

.timetable-container {
  background: transparent;
}

.header-section {
  background: white;
}

.header-text h1 {
  background: linear-gradient(45deg, var(--v-primary-base), var(--v-primary-lighten1));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}

.filter-select {
  min-width: 200px;
  max-width: 300px;
}

.content-section {
  background: transparent;
}

.timetable-grid {
  margin-top: 16px;
}

.timetable-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: white;
  position: relative;
  overflow: hidden;
}

.timetable-card--pending {
  opacity: 0.7;
  background: #f5f5f5 !important;
  cursor: not-allowed;
}

.timetable-card--pending:hover {
  transform: none;
}

.timetable-card--pending .v-card__title,
.timetable-card--pending .v-card__text {
  color: rgba(0, 0, 0, 0.6);
}

.timetable-card--pending .v-icon {
  color: rgba(0, 0, 0, 0.4) !important;
}

.timetable-card--pending .v-chip {
  opacity: 0.8;
}

.timetable-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(45deg, var(--v-primary-base), var(--v-primary-lighten1));
}

.timetable-card:hover {
  transform: translateY(-8px);
}

.timetable-card--pending {
  opacity: 0.7;
  background: #f5f5f5 !important;
  cursor: not-allowed;
}

.timetable-card--pending:hover {
  transform: none;
}

.timetable-card--pending .v-card__title,
.timetable-card--pending .v-card__text {
  color: rgba(0, 0, 0, 0.6);
}

.timetable-card--pending .v-icon {
  color: rgba(0, 0, 0, 0.4) !important;
}

.timetable-card .v-card__title {
  word-break: break-word;
  line-height: 1.4;
  padding: 16px;
}

.timetable-card .v-card__text {
  flex-grow: 1;
  padding: 16px;
}

.empty-state {
  background: white;
  border-radius: 12px;
  margin: 16px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .header-section {
    padding: 16px !important;
  }

  .filter-select {
    width: 100%;
    max-width: none;
  }
}
</style>